import { useAuth } from '@/context/AuthContext';
import Layout from '@/components/Layout';
import { motion } from "framer-motion";
import { HiOutlineSparkles } from "react-icons/hi";
import { useEffect, useState } from 'react';
import axios from '../utils/axios';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useNavigate } from 'react-router-dom';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import { TaskListItem } from '@/components/TaskListItem';
import { ProjectListItem } from '@/components/ProjectListItem';
import { MonitorListItem } from '@/components/MonitorListItem';
import { Skeleton } from "@/components/ui/skeleton";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area"

const fadeInVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: { opacity: 1, scale: 1 }
};

import { SEO } from '@/components/SEO';
import StatusBotIcon from '@/components/StatusBotIcon';

// Ai Command Palette
import { useAiPageContext } from '@/context/AiPageContextProvider';
import { contextHelpers } from "@/components/AiCommandPalette";

// Welcome Row Component
const WelcomeRow = ({ loading, error, data }) => {
    if (loading) {
        return <WelcomeLoadingSkeleton />;
    }

    if (error) {
        return (
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-12 mt-4">
                <div className="text-destructive">Failed to load welcome message</div>
            </div>
        );
    }

    if (!data) return null;

    return (
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-12 mt-4">
            <h1 className="text-3xl sm:text-4xl md:text-5xl tracking-tight">
                {data.welcomeMessage}
            </h1>
            
            <div className="text-sm text-muted-foreground shrink-0">
                <TooltipProvider delayDuration={0}>
                    <Tooltip>
                        <TooltipTrigger className="text-sm text-muted-foreground hover:text-primary cursor-pointer transition-colors">
                            {(() => {
                                const { weekNumber, daysRemaining, hoursRemaining } = data.weekData;
                                if (daysRemaining < 1) {
                                    return `Week ${weekNumber} • ${hoursRemaining} hour${hoursRemaining !== 1 ? 's' : ''} left`;
                                }
                                return `Week ${weekNumber} • ${daysRemaining} day${daysRemaining !== 1 ? 's' : ''} left`;
                            })()}
                        </TooltipTrigger>
                        <TooltipContent>
                            {`${data.weekData.startDate} – ${data.weekData.endDate}`}
                        </TooltipContent>
                    </Tooltip>
                </TooltipProvider>
            </div>
        </div>
    );
};

// Bot Insight Component
const BotInsight = ({ loading, error, data, onRefresh }) => {
    const [refreshing, setRefreshing] = useState(false);

    const handleRefresh = async () => {
        if (refreshing) return;
        setRefreshing(true);
        try {
            await onRefresh();
        } finally {
            setRefreshing(false);
        }
    };

    const formatTimestamp = (timestamp) => {
        if (!timestamp) return '';
        const date = new Date(timestamp);
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / (1000 * 60));
        
        if (diffMins < 1) return 'just now';
        if (diffMins < 60) return `${diffMins}m ago`;
        
        const diffHours = Math.floor(diffMins / 60);
        if (diffHours < 24) return `${diffHours}h ago`;
        
        const diffDays = Math.floor(diffHours / 24);
        if (diffDays < 7) return `${diffDays}d ago`;
        
        return date.toLocaleDateString();
    };

    if (loading) {
        return <InsightLoadingSkeleton />;
    }

    if (error) {
        return (
            <Card className="mb-12 relative overflow-hidden border-0 bg-gradient-to-br from-violet-600 to-indigo-600 shadow-xl">
                <CardContent className="relative px-6 py-8">
                    <div className="flex items-start justify-between">
                        <div className="flex-1">
                            <div className="flex items-center gap-2 mb-3">
                                <StatusBotIcon className="w-5 h-5 text-white/80" />
                                <span className="text-sm text-white/60 font-medium">Bot Insight</span>
                            </div>
                            <p className="text-base leading-relaxed text-white/85">
                                Unable to load insight message, check back later.
                            </p>
                        </div>
                    </div>
                </CardContent>
            </Card>
        );
    }

    if (!data?.message) return null;

    return (
        <Card className="mb-12 relative overflow-hidden border-0 bg-gradient-to-br from-violet-600 to-indigo-600 shadow-xl">
            <CardContent className="relative px-6 py-8 group">
                <div className="flex items-start justify-between">
                    <div className="flex-1">
                        <div className="flex items-center gap-2 mb-3">
                            <StatusBotIcon className="w-5 h-5 text-white/80" />
                            <span className="text-sm text-white/60 font-medium">Bot Insight</span>
                            {data.timestamp && (
                                <div className="flex items-center gap-1">
                                    <span className="text-xs text-white/40">•</span>
                                    <span className="text-xs text-white/40">{formatTimestamp(data.timestamp)}</span>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={handleRefresh}
                                        disabled={refreshing}
                                        className="text-white/40 hover:text-white/60 hover:bg-white/10 h-4 w-4 p-0 ml-1"
                                    >
                                        {refreshing ? (
                                            <div className="w-3 h-3 border border-white/30 border-t-white rounded-full animate-spin" />
                                        ) : (
                                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                            </svg>
                                        )}
                                    </Button>
                                </div>
                            )}
                        </div>
                        <div className="relative mt-1">
                            <p className="text-base leading-relaxed text-white/85 transition-all duration-200 group-hover:blur-sm">
                                {data.message}
                            </p>
                            <div className="pointer-events-none absolute inset-0 flex items-center justify-center opacity-0 transition-opacity duration-200 group-hover:opacity-100">
                                <button
                                    type="button"
                                    onClick={() => {
                                        const preset = `Can you explain or expand on this insight, and suggest what I should do next?`;
                                        const event = new CustomEvent('chat-with-statusbot', { detail: { query: preset } });
                                        window.dispatchEvent(event);
                                    }}
                                    className="pointer-events-auto inline-flex items-center rounded-lg px-4 py-2 bg-[#846ee7] hover:bg-[#907bea] text-white border border-white/20 shadow-sm transition-all duration-200"
                                    aria-label="Chat with StatusBot about this insight"
                                >
                                    <StatusBotIcon className="h-4 w-4 mr-2" />
                                    <span className="text-sm font-medium">Explain and expand</span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </CardContent>
        </Card>
    );
};

// Dashboard Cards Component
const DashboardCards = ({ loading, error, data, navigate }) => {
    if (loading) {
        return <CardLoadingSkeleton />;
    }

    if (error) {
        return (
            <Card>
                <CardContent className="px-6 py-8">
                    <div className="text-destructive">Failed to load dashboard data</div>
                </CardContent>
            </Card>
        );
    }

    if (!data) return null;

    return (
        <div className="space-y-6 sm:space-y-12">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-8">
                {/* Assigned Tasks Card */}
                <Card className="col-span-1">
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
                        <CardTitle className="text-sm font-medium">Assigned Tasks</CardTitle>
                        <Button variant="ghost" size="sm" onClick={() => navigate('/plan/tasks?myTasks=1')}>
                            View All
                        </Button>
                    </CardHeader>
                    <CardContent>
                        <ScrollArea className="w-full">
                            {data.assignedTasks?.length > 0 ? (
                                <div className="space-y-4">
                                    {data.assignedTasks.map(task => (
                                        <TaskListItem
                                            key={task.id}
                                            task={task}
                                            organizationSlug={task.organization.slug}
                                            columnConfig={{
                                                checkbox: false,
                                                key: true,
                                                summary: true,
                                                assignee: false,
                                                priority: true,
                                                dueDate: false,
                                                actions: false
                                            }}
                                            className="min-w-[400px]"
                                        />
                                    ))}
                                </div>
                            ) : (
                                <div className="flex flex-col items-center justify-center py-12 text-center">
                                    <p className="text-sm text-muted-foreground mb-4">
                                        No tasks currently assigned to you
                                    </p>
                                    <Button variant="outline" size="sm" onClick={() => navigate('/plan')}>
                                        Browse projects
                                    </Button>
                                </div>
                            )}
                            <ScrollBar orientation="horizontal" />
                        </ScrollArea>
                    </CardContent>
                </Card>

                {/* Projects Card */}
                <Card className="col-span-1">
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
                        <CardTitle className="text-sm font-medium">Recent Projects</CardTitle>
                        <Button variant="ghost" size="sm" onClick={() => navigate('/plan')}>
                            View All
                        </Button>
                    </CardHeader>
                    <CardContent>
                        <ScrollArea className="w-full">
                            {data?.projects?.length > 0 ? (
                                <div className="space-y-4">
                                    {data.projects.map(project => (
                                        <ProjectListItem
                                            key={project.id}
                                            project={project}
                                            columnConfig={{
                                                name: true,
                                                key: true,
                                                organization: true,
                                                taskCount: true,
                                                role: false,
                                                actions: false
                                            }}
                                        />
                                    ))}
                                </div>
                            ) : (
                                <div className="flex flex-col items-center justify-center py-12 text-center">
                                    <p className="text-sm text-muted-foreground mb-4">
                                        No recent projects in your organizations
                                    </p>
                                    <Button variant="outline" size="sm" onClick={() => navigate('/plan/new')}>
                                        Create a new project
                                    </Button>
                                </div>
                            )}
                            <ScrollBar orientation="horizontal" />
                        </ScrollArea>
                    </CardContent>
                </Card>
            </div>

            {/* Monitors Card */}
            <div className="grid grid-cols-1">
                <Card className="col-span-1">
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
                        <CardTitle className="text-sm font-medium">Active Monitors</CardTitle>
                        <Button variant="ghost" size="sm" onClick={() => navigate('/monitors')}>
                            View All
                        </Button>
                    </CardHeader>
                    <CardContent>
                        <ScrollArea className="w-full whitespace-nowrap">
                            {data?.monitors?.length > 0 ? (
                                <div className="space-y-4">
                                    {data.monitors.map(monitor => (
                                        <MonitorListItem
                                            key={monitor.id}
                                            monitor={monitor}
                                            columnConfig={{
                                                status: true,
                                                name: true,
                                                responseTime: true,
                                                lastRun: true,
                                                actions: true
                                            }}
                                        />
                                    ))}
                                </div>
                            ) : (
                                <div className="flex flex-col items-center justify-center py-12 text-center">
                                    <p className="text-sm text-muted-foreground mb-4">
                                        Keep track of your services in real-time
                                    </p>
                                    <Button variant="outline" size="sm" onClick={() => navigate('/monitors/new')}>
                                        Set up monitoring
                                    </Button>
                                </div>
                            )}
                            <ScrollBar orientation="horizontal" />
                        </ScrollArea>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
};

// Welcome Message Loading Skeleton
const WelcomeLoadingSkeleton = () => (
    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-12 mt-4">
        <Skeleton className="h-10 sm:h-12 w-[270px] sm:w-[400px]" />
        <Skeleton className="h-5 sm:h-6 w-[90px] sm:w-[150px]" />
    </div>
);

// Bot Insight Loading Skeleton
const InsightLoadingSkeleton = () => (
    <Card className="mb-12 relative overflow-hidden border-0 bg-gradient-to-br from-violet-600 to-indigo-600 shadow-xl">
        <CardContent className="relative px-6 py-8">
            <div className="flex items-start justify-between">
                <div className="flex-1">
                    <div className="flex items-center gap-2 mb-3">
                        <StatusBotIcon className="w-5 h-5 text-white/80" />
                        <Skeleton className="h-4 w-20 bg-white/20" />
                        <div className="flex items-center gap-1">
                            <span className="text-xs text-white/40">•</span>
                            <Skeleton className="h-3 w-12 bg-white/20" />
                            <Skeleton className="h-3 w-3 bg-white/20 ml-1" />
                        </div>
                    </div>
                    <div className="space-y-2">
                        <Skeleton className="h-5 w-[90%] bg-white/20" />
                        <Skeleton className="h-5 w-[75%] bg-white/20" />
                    </div>
                </div>
            </div>
        </CardContent>
    </Card>
);

// Card Section Loading Skeleton
const CardLoadingSkeleton = () => (
    <div className="space-y-6 sm:space-y-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-8">
            {/* Tasks Card Skeleton */}
            <Card className="col-span-1">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
                    <CardTitle className="text-sm font-medium">Assigned Tasks</CardTitle>
                    <Button variant="ghost" size="sm" disabled>View All</Button>
                </CardHeader>
                <CardContent>
                    <div className="space-y-4">
                        {[...Array(3)].map((_, i) => (
                            <Skeleton key={i} className="h-16 w-full" />
                        ))}
                    </div>
                </CardContent>
            </Card>

            {/* Projects Card Skeleton */}
            <Card className="col-span-1">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
                    <CardTitle className="text-sm font-medium">Recent Projects</CardTitle>
                    <Button variant="ghost" size="sm" disabled>View All</Button>
                </CardHeader>
                <CardContent>
                    <div className="space-y-4">
                        {[...Array(3)].map((_, i) => (
                            <Skeleton key={i} className="h-16 w-full" />
                        ))}
                    </div>
                </CardContent>
            </Card>
        </div>

        {/* Monitors Card Skeleton */}
        <Card className="w-full">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
                <CardTitle className="text-sm font-medium">Active Monitors</CardTitle>
                <Button variant="ghost" size="sm" disabled>View All</Button>
            </CardHeader>
            <CardContent>
                <div className="space-y-4">
                    {[...Array(3)].map((_, i) => (
                        <Skeleton key={i} className="h-16 w-full" />
                    ))}
                </div>
            </CardContent>
        </Card>
    </div>
);

const TodayPage = () => {
    const { user, axiosInstance } = useAuth();
    const navigate = useNavigate();
    const { setCurrentAiPageContext } = useAiPageContext();
    
    // Separate state for each data type
    const [basicLoading, setBasicLoading] = useState(true);
    const [insightLoading, setInsightLoading] = useState(true);
    const [cardsLoading, setCardsLoading] = useState(true);
    const [basicData, setBasicData] = useState(null);
    const [insightData, setInsightData] = useState(null);
    const [cardsData, setCardsData] = useState(null);

    const fetchInsightData = async () => {
        try {
            setInsightLoading(true);
            const response = await axiosInstance.get('/today/insight');
            setInsightData(response.data);
        } catch (error) {
            console.error('Error fetching insight data:', error);
        } finally {
            setInsightLoading(false);
        }
    };

    const refreshInsight = async () => {
        try {
            setInsightLoading(true);
            const response = await axiosInstance.post('/today/insight/refresh');
            setInsightData(response.data);
        } catch (error) {
            console.error('Error refreshing insight:', error);
        } finally {
            setInsightLoading(false);
        }
    };

    // Fetch data on mount
    useEffect(() => {
        const fetchData = async () => {
            try {
                const basicPromise = axiosInstance.get('/today/basic')
                    .then(response => {
                        setBasicData(response.data);
                        setBasicLoading(false);
                    })
                    .catch(error => {
                        console.error('Error fetching basic data:', error);
                        setBasicLoading(false);
                    });

                const cardsPromise = axiosInstance.get('/today/cards')
                    .then(response => {
                        setCardsData(response.data);
                        setCardsLoading(false);
                    })
                    .catch(error => {
                        console.error('Error fetching cards data:', error);
                        setCardsLoading(false);
                    });

                // Fetch insight data separately
                await fetchInsightData();

                // Wait for other requests to complete
                await Promise.all([basicPromise, cardsPromise]);

            } catch (error) {
                console.error('Error setting up requests:', error);
            }
        };

        fetchData();
    }, [axiosInstance]);

    // Set or update the AI page context on mount and when insight data changes
    useEffect(() => {
        setCurrentAiPageContext(
            contextHelpers.createCustomContext(
                'today',
                'Today',
                {
                    insightMessage: insightData?.message || null
                }
            )
        );
    }, [insightData, setCurrentAiPageContext]);

    return (
        <Layout>
            <SEO title="Today" />
            <div className="mx-auto space-y-6 pb-8">
                <motion.div
                    initial="hidden"
                    animate="visible"
                    variants={fadeInVariants}
                    transition={{ duration: 0.5 }}
                >
                    <WelcomeRow 
                        loading={basicLoading} 
                        error={basicData === null && !basicLoading} 
                        data={basicData} 
                    />
                    
                    <BotInsight 
                        loading={insightLoading} 
                        error={insightData === null && !insightLoading} 
                        data={insightData}
                        onRefresh={refreshInsight}
                    />
                    
                    <DashboardCards 
                        loading={cardsLoading} 
                        error={cardsData === null && !cardsLoading} 
                        data={cardsData}
                        navigate={navigate}
                    />
                </motion.div>
            </div>
        </Layout>
    );
};

export default TodayPage;
